import React, { useState, useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { renderButtons } from './render';
import { Directions, Phone } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface ICustomerListSingleRow {
    data: Array<any>;
    initialRenderCount?: number; // Control initial items
    loadMoreCount?: number; // How many more items to load on scroll
}

const CustomerListSingleRow: React.FC<ICustomerListSingleRow> = ({ data, initialRenderCount = 50, loadMoreCount = 15 }) => {
    const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
    const [renderedCount, setRenderedCount] = useState(initialRenderCount);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const navigate = useNavigate();

    const displayedData = useMemo(() => data.slice(0, renderedCount), [data, renderedCount]);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                let hasNewVisible = false;
                const newVisibleItems = new Set(visibleItems);

                entries.forEach((entry) => {
                    const index = Number(entry.target.getAttribute('data-index'));
                    if (entry.isIntersecting) {
                        newVisibleItems.add(index);
                        if (index >= renderedCount - 1) {
                            hasNewVisible = true;
                        }
                    } else {
                        newVisibleItems.delete(index);
                    }
                });

                setVisibleItems(new Set(newVisibleItems));

                if (hasNewVisible && renderedCount < data.length) {
                    setRenderedCount((prev) => Math.min(prev + loadMoreCount, data.length));
                }
            },
            { root: null, rootMargin: '100px', threshold: 0.1 }
        );

        itemRefs.current.forEach((el) => {
            if (el) observerRef.current?.observe(el);
        });

        return () => observerRef.current?.disconnect();
    }, [displayedData, visibleItems, renderedCount, data.length]);

    return (
        <>
            {displayedData.map((item, index) => (
                <Box
                    key={index}
                    ref={(el) => (itemRefs.current[index] = el)}
                    data-index={index}
                    sx={{
                        boxShadow: 1,
                        backgroundColor: 'grey.100',
                        padding: 1,
                        borderRadius: '15px',
                        minHeight: '50px' // Prevent layout shifts
                    }}
                    onClick={() => navigate('/mobileCustomerMenu?id=' + item.id)}
                >
                    {visibleItems.has(index) ? (
                        <>
                            <Box component={'p'} sx={{ fontSize: '11px', color: 'black.300', margin: 0 }}>
                                {item.topMetadata}
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    color: 'black',
                                    fontWeight: 'bold',
                                    minHeight: '25px',
                                    fontSize: '16px',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <span>{item.name}</span>
                                <p style={{ marginBottom: '0px', fontSize: '13px', color: 'black.300', fontWeight: 'lighter' }}>
                                    {item.address}
                                </p>
                                <Box sx={{ marginLeft: 'auto', display: 'flex' }}>
                                    {renderButtons([
                                        {
                                            icon: <Directions fontSize="small" sx={{ color: 'green' }} />,
                                            onClick: () => console.log('Directions button clicked')
                                        },
                                        {
                                            icon: <Phone fontSize="small" sx={{ color: 'red' }} />,
                                            onClick: () => console.log('Phone button clicked')
                                        }
                                    ]).map((button, buttonIndex) => (
                                        <span key={buttonIndex}>{button}</span>
                                    ))}
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '15px' }} />
                    )}
                </Box>
            ))}
        </>
    );
};

export default CustomerListSingleRow;
